import React, { useState, useEffect } from "react";
import {
  PencilIcon,
  CheckIcon,
  XIcon,
  TrashIcon,
  PlusIcon,
} from "lucide-react";
import { db } from "../../config/firebaseConfig";
import { ref, set, get } from "firebase/database";
import { useToast } from "../../@/components/ui/use-toast";
import SortupDialog from "../../components/common/SortupDialog";
import LayoutSkeleton from "../../components/skeletons/LayoutSkeleton";

interface CredentialSection {
  title: string;
  content: string;
}

interface CredentialsData {
  introduction: string;
  sections: CredentialSection[];
}

const EditCredentials: React.FC = () => {
  // State for storing credentials data
  const [credentialsData, setCredentialsData] = useState<CredentialsData>({
    introduction: "",
    sections: [],
  });

  // State for editing introduction
  const [isEditingIntro, setIsEditingIntro] = useState(false);
  const [editIntroText, setEditIntroText] = useState("");

  // State for editing section
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(
    null
  );
  const [editSectionTitle, setEditSectionTitle] = useState("");
  const [editSectionContent, setEditSectionContent] = useState("");

  // State for adding new section
  const [isAddingSection, setIsAddingSection] = useState(false);

  // Loading state
  const [loading, setLoading] = useState(true);

  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState<number | null>(null);

  useEffect(() => {
    // Fetch credentials data from Firebase
    const fetchCredentialsData = async () => {
      try {
        const credentialsRef = ref(db, "LayoutSettingData/Credentials");
        const snapshot = await get(credentialsRef);

        if (snapshot.exists()) {
          setCredentialsData(snapshot.val());
        } else {
          // If no data exists, use default content
          const defaultData: CredentialsData = {
            introduction:
              "Your login credentials are a vital part of protecting your account. Here's how we manage and secure your credentials.",
            sections: [
              {
                title: "Account Information",
                content:
                  "When you create an account on our platform, you will be required to choose a unique username and password. This information must be kept confidential, and you are responsible for all activities that occur under your account.",
              },
              {
                title: "Password Security",
                content:
                  "We recommend using a strong password that contains at least eight characters, including uppercase letters, lowercase letters, numbers, and symbols. Avoid using easily guessable information such as birthdays or common phrases.",
              },
              {
                title: "Two-Factor Authentication (2FA)",
                content:
                  "To enhance your account security, we offer two-factor authentication (2FA). By enabling 2FA, you will be required to enter a one-time passcode (OTP) sent to your phone or email in addition to your password when logging in.",
              },
              {
                title: "Forgotten Credentials",
                content:
                  'If you forget your password, you can reset it by using the "Forgot Password" option on the login page. A password reset link will be sent to your registered email address.',
              },
              {
                title: "Updating Credentials",
                content:
                  "You can update your password or email address at any time by going to the account settings page. We recommend updating your password regularly for enhanced security.",
              },
              {
                title: "Contact Us",
                content:
                  "If you have any concerns about your account security or believe your credentials have been compromised, please contact our support team immediately at admin@afrocart.ca.",
              },
            ],
          };
          setCredentialsData(defaultData);
          await saveToFirebase(defaultData);
        }
      } catch (error) {
        console.error("Error fetching credentials data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCredentialsData();
  }, []);

  // Save data to Firebase
  const saveToFirebase = async (data = credentialsData) => {
    try {
      const credentialsRef = ref(db, "LayoutSettingData/Credentials");
      await set(credentialsRef, data);
      toast({
        title: "Success",
        description: "Changes saved successfully",
        variant: "success",
        showIcon: true,
      });
      return true;
    } catch (error) {
      console.error("Error saving credentials data:", error);
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive",
        showIcon: true,
      });
      return false;
    }
  };

  // Handle editing introduction
  const handleEditIntro = () => {
    setEditIntroText(credentialsData.introduction);
    setIsEditingIntro(true);
  };

  const handleSaveIntro = async () => {
    const updatedData = { ...credentialsData, introduction: editIntroText };
    const success = await saveToFirebase(updatedData);
    if (success) {
      setCredentialsData(updatedData);
      setIsEditingIntro(false);
    }
  };

  // Handle editing section
  const handleEditSection = (index: number) => {
    const section = credentialsData.sections[index];
    setEditingSectionIndex(index);
    setEditSectionTitle(section.title);
    setEditSectionContent(section.content);
  };

  const handleSaveSection = async () => {
    if (editingSectionIndex !== null) {
      const updatedSections = [...credentialsData.sections];
      updatedSections[editingSectionIndex] = {
        title: editSectionTitle,
        content: editSectionContent,
      };

      const updatedData = {
        ...credentialsData,
        sections: updatedSections,
      };

      const success = await saveToFirebase(updatedData);
      if (success) {
        setCredentialsData(updatedData);
        setEditingSectionIndex(null);
      }
    }
  };

  // Handle adding new section
  const handleAddSection = () => {
    setEditSectionTitle("");
    setEditSectionContent("");
    setIsAddingSection(true);
  };

  const handleSaveNewSection = async () => {
    const newSection: CredentialSection = {
      title: editSectionTitle,
      content: editSectionContent,
    };

    const updatedData = {
      ...credentialsData,
      sections: [...credentialsData.sections, newSection],
    };

    const success = await saveToFirebase(updatedData);
    if (success) {
      setCredentialsData(updatedData);
      setIsAddingSection(false);
    }
  };

  // Handle deleting section
  const handleDeleteSection = (index: number) => {
    setSectionToDelete(index);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (sectionToDelete !== null) {
      const updatedSections = credentialsData.sections.filter(
        (_, i) => i !== sectionToDelete
      );

      const updatedData = {
        ...credentialsData,
        sections: updatedSections,
      };

      const success = await saveToFirebase(updatedData);
      if (success) {
        setCredentialsData(updatedData);
        toast({
          title: "Success",
          description: "Section deleted successfully",
          variant: "success",
          showIcon: true,
        });
      }
    }
    setShowDeleteDialog(false);
    setSectionToDelete(null);
  };

  // Cancel editing
  const handleCancel = () => {
    setIsEditingIntro(false);
    setEditingSectionIndex(null);
    setIsAddingSection(false);
  };

  // Render introduction section
  const renderIntroduction = () => {
    return (
      <div className="bg-gray-50 rounded-lg p-6">
        {isEditingIntro ? (
          <div className="space-y-4">
            <textarea
              value={editIntroText}
              onChange={(e) => setEditIntroText(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
            />
            <div className="flex space-x-3">
              <button
                onClick={handleSaveIntro}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start">
            <p className="leading-relaxed">{credentialsData.introduction}</p>
            <button
              className="text-customGreenColor hover:text-opacity-80 transition-all ml-3 flex-shrink-0"
              onClick={handleEditIntro}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    );
  };

  // Render section
  const renderSection = (section: CredentialSection, index: number) => {
    return (
      <div key={index} className="bg-gray-50 rounded-lg p-6 shadow-sm">
        {editingSectionIndex === index ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Title
              </label>
              <input
                type="text"
                value={editSectionTitle}
                onChange={(e) => setEditSectionTitle(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Content
              </label>
              <textarea
                value={editSectionContent}
                onChange={(e) => setEditSectionContent(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
              />
            </div>
            <div className="flex space-x-3 pt-2">
              <button
                onClick={handleSaveSection}
                className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
              >
                <CheckIcon className="h-4 w-4 mr-2" /> Save
              </button>
              <button
                onClick={handleCancel}
                className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <XIcon className="h-4 w-4 mr-2" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-helveticaBold text-2xl">{section.title}</h2>
              <div className="flex space-x-3">
                <button
                  className="text-customGreenColor hover:text-opacity-80 transition-all"
                  onClick={() => handleEditSection(index)}
                >
                  <PencilIcon className="h-5 w-5" />
                </button>
                <button
                  className="text-red-500 hover:text-opacity-80 transition-all"
                  onClick={() => handleDeleteSection(index)}
                >
                  <TrashIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="whitespace-pre-line leading-relaxed">
              {section.content}
            </p>
          </>
        )}
      </div>
    );
  };

  if (loading) {
    return <LayoutSkeleton />;
  }

  return (
    <div className="max-w-[1400px] mx-auto bg-white p-6">
      <div className="w-full flex flex-col font-normal text-sm space-y-6 text-black">
        <div className="flex gap-4 flex-col md:flex-row justify-between items-center w-full mb-8">
          <h1 className="font-helveticaBold text-3xl md:text-4xl text-black">
            Account Credentials
          </h1>
          <button
            className="text-white w-full md:w-auto bg-customGreenColor justify-center hover:bg-opacity-90 transition-all px-4 py-2 rounded-md flex items-center"
            onClick={handleAddSection}
          >
            <PlusIcon className="h-4 w-4 mr-2" /> Add Section
          </button>
        </div>

        {/* Introduction section */}
        {renderIntroduction()}

        {/* Add New Section Form */}
        {isAddingSection && (
          <div className="bg-gray-50 p-6 rounded-lg shadow-sm">
            <h3 className="font-helveticaBold text-black text-xl mb-4">
              Add New Section
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Section Title
                </label>
                <input
                  type="text"
                  value={editSectionTitle}
                  onChange={(e) => setEditSectionTitle(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter section title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Content
                </label>
                <textarea
                  value={editSectionContent}
                  onChange={(e) => setEditSectionContent(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-customGreenColor focus:border-transparent"
                  placeholder="Enter section content"
                />
              </div>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={handleSaveNewSection}
                  className="bg-customGreenColor hover:bg-opacity-90 transition-all text-white px-4 py-2 rounded-md flex items-center"
                >
                  <CheckIcon className="h-4 w-4 mr-2" /> Save
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-200 hover:bg-gray-300 transition-all text-gray-700 px-4 py-2 rounded-md flex items-center"
                >
                  <XIcon className="h-4 w-4 mr-2" /> Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sections List */}
        <div className="grid gap-6">
          {credentialsData.sections.map((section, index) =>
            renderSection(section, index)
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <SortupDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSectionToDelete(null);
        }}
        header="Confirm Delete"
        footerButtons={[
          {
            name: "Delete",
            onClick: confirmDelete,
            disabled: false,
            className: "bg-customGreenColor text-white hover:bg-opacity-90",
          },
          {
            name: "Cancel",
            onClick: () => {
              setShowDeleteDialog(false);
              setSectionToDelete(null);
            },
            disabled: false,
            className: "bg-gray-200 text-gray-700 hover:bg-gray-300",
          },
        ]}
      >
        <p>
          Are you sure you want to delete this section? This action cannot be
          undone.
        </p>
      </SortupDialog>
    </div>
  );
};

export default EditCredentials;
